import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Habit, DayEntry } from '../../types/habit';
import Checkbox from './Checkbox';
import StarRating from './StarRating';
import './HabitTable.scss';

export interface HabitTableRow {
  key: string;
  day: number;
  dayNum?: number;
  dow?: string;
  isToday?: boolean;
  isWeekend?: boolean;
}

interface Props {
  habits: Habit[];
  rows: HabitTableRow[];
  getEntry: (habitId: string, day: number) => DayEntry | undefined;
  onToggle: (habitId: string, day: number) => void;
  onRate: (habitId: string, day: number, rating: number) => void;
  /** Show day number and weekday columns (monthly view) */
  showDayColumns?: boolean;
  /** Show edit buttons on header (monthly view) */
  headerActions?: (habit: Habit) => React.ReactNode;
  /** Extra th at end (e.g. add column) */
  headerExtra?: React.ReactNode;
  /** Extra td at end of each row */
  rowExtra?: React.ReactNode;
  /** Summary row at bottom */
  summaryRow?: (habit: Habit) => React.ReactNode;
  /** Drag handlers on headers */
  onHeaderDragStart?: (habitId: string) => void;
  onHeaderDragEnter?: (habitId: string) => void;
  onHeaderDragOver?: (e: React.DragEvent) => void;
  onHeaderDrop?: (habitId: string) => void;
  onHeaderDragEnd?: () => void;
  draggedId?: string | null;
  dragOverId?: string | null;
  /** Star size */
  starSize?: number;
  /** Read-only mode */
  readOnly?: boolean;
  /** Compact painted 0–5 grid (monthly stats) */
  compact?: boolean;
  /** Returns true if a cell should be hidden (habit didn't exist yet) */
  isDisabledCell?: (habitId: string, day: number) => boolean;
}

const scoreForEntry = (habit: Habit, entry: DayEntry | undefined): number => {
  if (habit.type === 'rating') return Math.min(5, Math.max(0, entry?.rating ?? 0));
  if (habit.type === 'checkbox') return entry?.completed ? 5 : 0;
  return 0;
};

const RATING_FILLS = [0, 22, 40, 58, 78, 100];

const HabitTable: React.FC<Props> = ({
  habits,
  rows,
  getEntry,
  onToggle,
  onRate,
  showDayColumns = false,
  headerActions,
  headerExtra,
  rowExtra,
  summaryRow,
  onHeaderDragStart,
  onHeaderDragEnter,
  onHeaderDragOver,
  onHeaderDrop,
  onHeaderDragEnd,
  draggedId,
  dragOverId,
  starSize = 10,
  readOnly = false,
  compact = false,
  isDisabledCell,
}) => {
  const { formatMessage: t } = useIntl();
  const wrapRef = useRef<HTMLDivElement>(null);
  const showDow = showDayColumns && !compact;
  const [daysOnTop, setDaysOnTop] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(min-width: 441px)').matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(min-width: 441px)');
    const apply = () => setDaysOnTop(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useLayoutEffect(() => {
    if (!compact) return;
    const wrap = wrapRef.current;
    const todayEl = wrap?.querySelector<HTMLElement>('[data-today="true"]');
    if (!wrap || !todayEl) return;
    const scroller = wrap.parentElement instanceof HTMLElement ? wrap.parentElement : wrap;
    const todayRect = todayEl.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    if (daysOnTop) {
      const sticky = wrap.querySelector<HTMLElement>('.ht-cell-habit-name');
      const stickyW = sticky?.getBoundingClientRect().width ?? 0;
      const visibleCenter = scrollerRect.left + stickyW + (scrollerRect.width - stickyW) / 2;
      scroller.scrollLeft += todayRect.left + todayRect.width / 2 - visibleCenter;
    } else {
      const stickyH = wrap.querySelector('thead')?.getBoundingClientRect().height ?? 0;
      const visibleCenter = scrollerRect.top + stickyH + (scrollerRect.height - stickyH) / 2;
      scroller.scrollTop += todayRect.top + todayRect.height / 2 - visibleCenter;
    }
  }, [compact, rows, daysOnTop]);

  const renderCompactCell = (habit: Habit, row: HabitTableRow) => {
    const entry = getEntry(habit.id, row.day);
    const isDisabled = isDisabledCell ? isDisabledCell(habit.id, row.day) : false;
    if (isDisabled) {
      return (
        <td key={`${habit.id}-${row.key}`} className="ht-cell ht-cell--disabled">
          <span className="ht-score ht-score--disabled" aria-hidden="true" />
        </td>
      );
    }
    const score = scoreForEntry(habit, entry);
    const fill = RATING_FILLS[score] ?? 0;
    const isEmpty = score === 0;
    return (
      <td key={`${habit.id}-${row.key}`} className="ht-cell ht-cell--score">
        <button
          type="button"
          className={[
            'ht-score',
            `ht-score--${score}`,
            score >= 4 ? 'ht-score--bright' : '',
            isEmpty ? 'ht-score--empty' : '',
          ].filter(Boolean).join(' ')}
          style={{
            '--ht-habit-color': habit.color,
            '--ht-fill': `${fill}%`,
          } as React.CSSProperties}
          disabled={readOnly}
          aria-label={`${habit.name} ${score}`}
          onClick={readOnly ? undefined : () => {
            if (habit.type === 'rating') {
              onRate(habit.id, row.day, score >= 5 ? 0 : score + 1);
            } else {
              onToggle(habit.id, row.day);
            }
          }}
        >
          {habit.type === 'rating' && score > 0 ? score : ''}
        </button>
      </td>
    );
  };

  if (compact && daysOnTop) {
    return (
      <div className="habit-table-wrap habit-table-wrap--compact" ref={wrapRef}>
        <table className="habit-table habit-table--compact">
          <thead>
            <tr>
              <th className="ht-col-habit-label" />
              {rows.map(row => (
                <th
                  key={row.key}
                  className={`ht-col-day-num${row.isToday ? ' ht-col-day-num--today' : ''}`}
                  data-today={row.isToday ? 'true' : undefined}
                >
                  {row.dayNum}
                </th>
              ))}
              {summaryRow && <th className="ht-col-avg">{t({ id: 'habits.avg' })}</th>}
            </tr>
          </thead>
          <tbody>
            {habits.map(habit => (
              <tr key={habit.id} className="ht-row">
                <th className="ht-cell-habit-name" title={habit.name}>
                  <span className="ht-habit-name">{habit.name}</span>
                </th>
                {rows.map(row => renderCompactCell(habit, row))}
                {summaryRow && (
                  <td
                    className={`ht-summary-cell${habit.temporary ? ' ht-summary-cell--temporary' : ''}`}
                    style={{ '--habit-color': habit.color } as React.CSSProperties}
                  >
                    {summaryRow(habit)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (compact) {
    const compactColCount = habits.length + 1;
    return (
      <div className="habit-table-wrap habit-table-wrap--compact" ref={wrapRef}>
        <table className="habit-table habit-table--compact habit-table--compact-rows">
          <colgroup>
            {Array.from({ length: compactColCount }, (_, i) => (
              <col key={i} className={i === 0 ? 'ht-compact-col ht-compact-col--day' : 'ht-compact-col'} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="ht-col-day-num" />
              {habits.map(habit => (
                <th key={habit.id} className="ht-col-habit" title={habit.name}>
                  <span className="ht-habit-clip">
                    <span className="ht-habit-name">{habit.name}</span>
                  </span>
                </th>
              ))}
            </tr>
            {summaryRow ? (
              <tr className="ht-summary-row">
                <th className="ht-summary-label">%</th>
                {habits.map(habit => (
                  <th
                    key={habit.id}
                    className={`ht-summary-cell${habit.temporary ? ' ht-summary-cell--temporary' : ''}`}
                    style={{ '--habit-color': habit.color } as React.CSSProperties}
                  >
                    {summaryRow(habit)}
                  </th>
                ))}
              </tr>
            ) : null}
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key} className={`ht-row${row.isToday ? ' ht-row--today' : ''}`}>
                <td
                  className={`ht-cell-day-num${row.isToday ? ' ht-cell-day-num--today' : ''}`}
                  data-today={row.isToday ? 'true' : undefined}
                >
                  {row.dayNum}
                </td>
                {habits.map(habit => renderCompactCell(habit, row))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={`habit-table-wrap${compact ? ' habit-table-wrap--compact' : ''}`}>
      <table className={`habit-table${compact ? ' habit-table--compact' : ''}`}>
        <thead>
          <tr>
            {showDayColumns && (
              <>
                <th className="ht-col-day-num">{compact ? '' : t({ id: 'year.monthly.dayColumn' })}</th>
                {showDow && <th className="ht-col-dow"></th>}
              </>
            )}
            {habits.map(habit => {
              const draggable = !!onHeaderDragStart;
              return (
                <th
                  key={habit.id}
                  draggable={draggable}
                  onDragStart={draggable ? () => onHeaderDragStart!(habit.id) : undefined}
                  onDragEnter={onHeaderDragEnter ? () => onHeaderDragEnter(habit.id) : undefined}
                  onDragOver={onHeaderDragOver}
                  onDrop={onHeaderDrop ? () => onHeaderDrop(habit.id) : undefined}
                  onDragEnd={onHeaderDragEnd}
                  className={[
                    'ht-col-habit',
                    habit.temporary ? 'ht-col-habit--temporary' : '',
                    habit.type === 'rating' ? 'ht-col-habit--rating' : '',
                    draggedId === habit.id ? 'ht-col-habit--dragging' : '',
                    dragOverId === habit.id && draggedId !== habit.id ? 'ht-col-habit--drag-over' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {headerActions ? headerActions(habit) : null}
                  <span className="ht-habit-name" title={habit.name}>{habit.name}</span>
                </th>
              );
            })}
            {headerExtra}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.key}
              className={[
                'ht-row',
                row.isToday ? 'ht-row--today' : '',
                row.isWeekend ? 'ht-row--weekend' : '',
              ].filter(Boolean).join(' ')}
            >
              {showDayColumns && (
                <>
                  <td className="ht-cell-day-num">{row.dayNum}</td>
                  {showDow && <td className="ht-cell-dow">{row.dow}</td>}
                </>
              )}
              {habits.map(habit => {
                const entry = getEntry(habit.id, row.day);
                const isDisabled = isDisabledCell ? isDisabledCell(habit.id, row.day) : false;
                if (isDisabled) {
                  return (
                    <td key={habit.id} className="ht-cell ht-cell--disabled" />
                  );
                }
                return (
                  <td
                    key={habit.id}
                    className={[
                      'ht-cell',
                      habit.temporary ? 'ht-cell--temporary' : '',
                      habit.type === 'rating' ? 'ht-cell--rating' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <div className={`ht-cell-inner${habit.type === 'rating' ? ' ht-cell-inner--rating' : ''}${readOnly ? ' ht-cell-inner--readonly' : ''}`}>
                      {habit.type === 'rating' ? (
                        <StarRating
                          rating={entry?.rating || 0}
                          onRate={readOnly ? undefined : r => onRate(habit.id, row.day, r)}
                          size={starSize}
                          readOnly={readOnly}
                          color={habit.color}
                        />
                      ) : (
                        <Checkbox
                          checked={entry?.completed || false}
                          onChange={readOnly ? undefined : () => onToggle(habit.id, row.day)}
                          color={habit.color}
                          readOnly={readOnly}
                        />
                      )}
                    </div>
                  </td>
                );
              })}
              {rowExtra}
            </tr>
          ))}

          {summaryRow && (
            <tr className="ht-summary-row">
              {showDayColumns && (
                <td className="ht-summary-label" colSpan={showDow ? 2 : 1}>{t({ id: 'habits.avg' })}</td>
              )}
              {habits.map(habit => (
                <td
                  key={habit.id}
                  className={[
                    'ht-summary-cell',
                    habit.temporary ? 'ht-summary-cell--temporary' : '',
                  ].filter(Boolean).join(' ')}
                  style={{ '--habit-color': habit.color } as React.CSSProperties}
                >
                  {summaryRow(habit)}
                </td>
              ))}
              {rowExtra}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default HabitTable;
